import { useEffect, useRef, useState, type ReactNode } from 'react';
import clsx from 'clsx';
import { X } from 'lucide-react';
import { Button, IconButton } from './ui.js';

/**
 * Modal built on the native <dialog> element.
 *
 * showModal() is what gives us the top layer, a real focus trap and Esc
 * handling for free — none of which a div-with-a-z-index gets right.
 * `closedby="any"` adds light dismiss declaratively where it is supported, and
 * the backdrop-click fallback below covers Safari.
 */
export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  dismissable = true,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  dismissable?: boolean;
}) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;

    const handleClose = () => onClose();
    const handleCancel = (e: Event) => {
      if (!dismissable) {
        e.preventDefault();
        return;
      }
      // Let the native close event drive state so Esc and the X agree.
    };

    dialog.addEventListener('close', handleClose);
    dialog.addEventListener('cancel', handleCancel);
    return () => {
      dialog.removeEventListener('close', handleClose);
      dialog.removeEventListener('cancel', handleCancel);
    };
  }, [onClose, dismissable]);

  // Light-dismiss fallback for browsers without `closedby` (Safari).
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog || !dismissable) return;
    if ('closedBy' in HTMLDialogElement.prototype) return;

    const onClick = (event: MouseEvent) => {
      if (event.target !== dialog) return;
      const rect = dialog.getBoundingClientRect();
      const insideContent =
        rect.top <= event.clientY &&
        event.clientY <= rect.top + rect.height &&
        rect.left <= event.clientX &&
        event.clientX <= rect.left + rect.width;
      if (!insideContent) dialog.close();
    };

    dialog.addEventListener('click', onClick);
    return () => dialog.removeEventListener('click', onClick);
  }, [dismissable]);

  const widths = {
    sm: 'max-w-sm',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
  } as const;

  return (
    <dialog
      ref={ref}
      // React types do not know `closedby` yet; the attribute is what matters.
      {...({ closedby: dismissable ? 'any' : 'none' } as Record<string, string>)}
      aria-labelledby="lp-modal-title"
      className={clsx(
        'w-[calc(100vw-2rem)] bg-surface text-ink rounded-card border border-line shadow-pop',
        'p-0 m-auto backdrop:bg-black/40',
        widths[size],
      )}
    >
      {open ? (
        <div className="flex flex-col max-h-[85vh]">
          <div className="flex items-start justify-between gap-4 px-5 py-4 border-b border-line">
            <div className="min-w-0">
              <h2 id="lp-modal-title" className="font-semibold text-ink">
                {title}
              </h2>
              {description ? (
                <p className="text-sm text-ink-muted mt-1 leading-relaxed">{description}</p>
              ) : null}
            </div>
            {dismissable ? (
              <IconButton label="Close" onClick={onClose}>
                <X size={16} />
              </IconButton>
            ) : null}
          </div>

          {children ? <div className="px-5 py-4 overflow-y-auto">{children}</div> : null}

          {footer ? (
            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-line bg-surface-2/50">
              {footer}
            </div>
          ) : null}
        </div>
      ) : null}
    </dialog>
  );
}

/**
 * Confirmation that names exactly what is about to happen. Used for publish
 * (which lists what goes live) and for the capstone reference solution (which
 * requires typing the confirmation word, so it can never be a stray click).
 */
export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel = 'Confirm',
  tone = 'primary',
  details,
  requirePhrase,
  loading,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  title: ReactNode;
  description?: ReactNode;
  confirmLabel?: string;
  tone?: 'primary' | 'danger';
  details?: ReactNode;
  requirePhrase?: string;
  loading?: boolean;
}) {
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setTyped('');
  }, [open]);

  const satisfied = !requirePhrase || typed.trim().toLowerCase() === requirePhrase.toLowerCase();

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      description={description}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy || loading}>
            Cancel
          </Button>
          <Button
            variant={tone === 'danger' ? 'danger' : 'primary'}
            disabled={!satisfied}
            loading={busy || loading}
            onClick={async () => {
              setBusy(true);
              try {
                await onConfirm();
              } finally {
                setBusy(false);
              }
            }}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      {details}
      {requirePhrase ? (
        <div className="mt-4">
          <label htmlFor="confirm-phrase" className="text-sm text-ink">
            Type <span className="font-mono font-semibold">{requirePhrase}</span> to continue
          </label>
          <input
            id="confirm-phrase"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            autoComplete="off"
            className="mt-1.5 w-full bg-surface border border-control-line rounded-control px-3 py-2 text-sm"
          />
        </div>
      ) : null}
    </Modal>
  );
}
